
import { useCallback } from 'react';
import { App } from 'antd';
import { smartCopywritingAPI } from '../services';
import type { CopywritingVersion, SmartCopywritingProject, CopywritingStep } from '../types';
import type { UseSmartCopywritingState } from './useSmartCopywritingState';

export const useProjectLogic = (
    state: UseSmartCopywritingState,
    setState: React.Dispatch<React.SetStateAction<UseSmartCopywritingState>>,
    setLoading: (key: keyof UseSmartCopywritingState['loading'], value: boolean) => void
) => {
    const { message } = App.useApp();

    const loadProject = useCallback(async (id: number) => {
        setLoading('project', true);
        try {
            const project = await smartCopywritingAPI.getProject(id);
            setState(prev => ({
                ...prev,
                project,
                productName: project.product_name || prev.productName,
                audiences: project.target_audiences || [],
                scenarios: project.usage_scenarios || [],
                currentStep: project.current_step as CopywritingStep,
            }));
            return project;
        } catch (error) {
            message.error('加载项目失败');
            throw error;
        } finally {
            setLoading('project', false);
        }
    }, [message, setLoading, setState]);

    const saveVersion = useCallback(async (notes?: string): Promise<CopywritingVersion | null> => {
        if (!state.project || !state.copyResult) return null;

        setLoading('saving', true);
        try {
            const result = await smartCopywritingAPI.saveVersion(state.project.id, {
                titles: state.copyResult.titles,
                bullet_points: state.copyResult.bullet_points,
                fabe_analysis: state.copyResult.fabe_analysis,
                notes,
            });

            message.success(result.message);
            return result.version;
        } catch (error) {
            message.error('保存失败');
            throw error;
        } finally {
            setLoading('saving', false);
        }
    }, [state.project, state.copyResult, message, setLoading]);

    return { loadProject, saveVersion };
};
